import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Loader2, Plus, FileText, Trash2, RefreshCw, Check, Clock, X, Eye, ExternalLink, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { WhatsAppTemplateBuilder } from './WhatsAppTemplateBuilder';
import type { WhatsAppTemplate, WhatsAppTemplateCreateRequest, Agent } from '@/api/tenants/tenants.types';
import { WHATSAPP_TEMPLATE_LANGUAGES } from '@/api/tenants/tenants.types';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';

interface TemplateManagementCardProps {
    templates: WhatsAppTemplate[];
    agents: Agent[];
    isLoading: boolean;
    onCreateTemplate: (template: WhatsAppTemplateCreateRequest) => Promise<void>;
    onDeleteTemplate: (templateId: number) => Promise<void>;
    onViewTemplate: (templateId: number) => Promise<void>;
    onRefreshTemplates: () => void;
}

export const TemplateManagementCard = ({ 
    templates, 
    agents,
    isLoading, 
    onCreateTemplate, 
    onDeleteTemplate,
    onViewTemplate,
    onRefreshTemplates
}: TemplateManagementCardProps) => {
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isDeleting, setIsDeleting] = useState<number | null>(null);
    const toast = useEnhancedToast();
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [formData, setFormData] = useState<WhatsAppTemplateCreateRequest>({
        name: '',
        friendly_name: '',
        group: '',
        category: 'MARKETING',
        components: [],
        agent_id: null,
        language: 'en' // Default to English
    });

    const whatsAppAgents = agents.filter(agent => agent.channel === 'whatsapp');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validate that name field is not empty
        if (!formData.name.trim()) {
            toast.error({
                icon: AlertCircle,
                title: "Missing Required Field",
                description: "Template name is required. Please enter a template name before submitting.",
                duration: 4000,
            });
            return;
        }
        
        // Validate that WhatsApp agents are available
        if (whatsAppAgents.length === 0) {
            toast.error({
                icon: AlertCircle,
                title: "No WhatsApp Account Connected",
                description: "At least one WhatsApp account should be connected before submitting templates.",
                duration: 4000,
            });
            return;
        }
        
        // Validate that an agent is selected
        if (!formData.agent_id) {
            toast.error({
                icon: AlertCircle,
                title: "WhatsApp Agent Required",
                description: "Please select a WhatsApp agent for this template before submitting.",
                duration: 4000,
            });
            return;
        }
        
        // Validate that body component is present
        const hasBodyComponent = formData.components.some(component => component.type === 'BODY');
        if (!hasBodyComponent) {
            toast.error({
                icon: AlertCircle,
                title: "Missing Required Component",
                description: "Template must include a BODY component. Please add one before submitting.",
                duration: 4000,
            });
            return;
        }
        
        setIsCreating(true);
        try {
            await onCreateTemplate(formData);
            setIsCreateDialogOpen(false);
            setFormData({
                name: '',
                friendly_name: '',
                group: '',
                category: 'MARKETING',
                components: [],
                agent_id: null,
                language: 'en'
            });
        } catch {
            // Error handling is done in parent component
        } finally {
            setIsCreating(false);
        }
    };

    const handleDelete = async (templateId: number) => {
        setIsDeleting(templateId);
        try {
            await onDeleteTemplate(templateId);
        } catch {
            // Error handling is done in parent component
        } finally {
            setIsDeleting(null);
        }
    };

    const handleRefresh = () => {
        setIsRefreshing(true);
        onRefreshTemplates();
    };

    // Reset refresh state when templates change (indicating refresh is complete)
    useEffect(() => {
        if (isRefreshing) {
            const timer = setTimeout(() => {
                setIsRefreshing(false);
            }, 500); // Minimum loading time for better UX
            return () => clearTimeout(timer);
        }
    }, [templates, isRefreshing]);

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'approved':
                return (
                    <Badge variant="default" className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700">
                        <Check className="w-3 h-3 mr-1" />
                        Approved
                    </Badge>
                );
            case 'pending':
            case 'submitted':
                return (
                    <Badge variant="secondary" className="bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-700">
                        <Clock className="w-3 h-3 mr-1" />
                        Pending
                    </Badge>
                );
            case 'rejected':
                return (
                    <Badge variant="destructive" className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700">
                        <X className="w-3 h-3 mr-1" />
                        Rejected
                    </Badge>
                );
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    const getAgentNames = (agentIds: number[]) => {
        if (!agentIds || agentIds.length === 0) {
            return 'No agents';
        }
        
        const names = agentIds
            .map(id => whatsAppAgents.find(agent => agent.id === id)?.name)
            .filter(Boolean);
        
        return names.length > 0 ? names.join(', ') : 'Unknown agent';
    };

    if (isLoading && !isRefreshing) {
        return (
            <Card className="shadow-sm">
                <CardHeader className="border-b bg-muted/20">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <FileText className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 space-y-1">
                                <CardTitle>WhatsApp Templates</CardTitle>
                                <CardDescription>
                                    Manage WhatsApp message templates for your organization
                                </CardDescription>
                            </div>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="animate-pulse">
                                <div className="h-12 bg-muted rounded"></div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <Card className="shadow-sm">
                <CardHeader className="border-b bg-muted/20">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <FileText className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 space-y-1">
                                <CardTitle>WhatsApp Templates</CardTitle>
                                <CardDescription>
                                    Manage WhatsApp message templates for your organization
                                </CardDescription>
                            </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleRefresh}
                                disabled={isRefreshing}
                                className="gap-2"
                            >
                                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                                Refresh
                            </Button>
                            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                                <DialogTrigger asChild>
                                    <Button size="sm" className="gap-2">
                                        <Plus className="h-4 w-4" />
                                        Submit Template
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-7xl max-h-[90vh] p-0 overflow-hidden shadow-xl">
                                    {/* Header */}
                                    <div className="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-950/40 dark:to-green-900/40 border-b dark:border-green-800/30 px-6 py-3">
                                        <div className="flex items-center space-x-3">
                                            <div className="h-10 w-10 rounded-lg bg-green-600 dark:bg-green-600 flex items-center justify-center shadow-sm">
                                                <FileText className="h-5 w-5 text-white" />
                                            </div>
                                            <div>
                                                <DialogTitle className="text-lg font-semibold">Submit WhatsApp Template</DialogTitle>
                                                <DialogDescription className="text-sm mt-0.5">
                                                    Submit a new WhatsApp template for approval. It will be reviewed by WhatsApp before becoming available.
                                                </DialogDescription>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Content */}
                                    <div className="px-6 pb-6 pt-2 space-y-6 max-h-[70vh] overflow-y-auto" style={{
                                        scrollbarWidth: 'thin',
                                        scrollbarColor: 'hsl(var(--muted-foreground) / 0.3) hsl(var(--muted) / 0.3)'
                                    }}>


                                        <form onSubmit={handleSubmit} className="space-y-6">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="template-name" className="text-sm font-medium">Template Name *</Label>
                                                <Input
                                                    id="template-name"
                                                    placeholder="welcome_message"
                                                    value={formData.name}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                                    className="h-9"
                                                    required
                                                />
                                                <p className="text-xs text-muted-foreground">Lowercase, a-z, 0-9, underscores only</p>
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="template-category" className="text-sm font-medium">Category *</Label>
                                                <Select 
                                                    value={formData.category} 
                                                    onValueChange={(value: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION') => setFormData(prev => ({ ...prev, category: value }))}
                                                >
                                                    <SelectTrigger className="h-9">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="MARKETING">Marketing</SelectItem>
                                                        <SelectItem value="UTILITY">Utility</SelectItem>
                                                        <SelectItem value="AUTHENTICATION">Authentication</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <p className="text-xs text-muted-foreground">Determines cost of sending messages with this template</p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <Label htmlFor="template-language" className="text-sm font-medium">Language *</Label>
                                                <Select 
                                                    value={formData.language} 
                                                    onValueChange={(value: string) => setFormData(prev => ({ ...prev, language: value }))}
                                                >
                                                    <SelectTrigger className="h-9">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent className="max-h-[300px]">
                                                        {WHATSAPP_TEMPLATE_LANGUAGES.map((lang) => (
                                                            <SelectItem key={lang.code} value={lang.code}>
                                                                {lang.name}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <p className="text-xs text-muted-foreground">Language of the template content</p>
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="template-agent" className="text-sm font-medium">WhatsApp Agent *</Label>
                                                <Select 
                                                    value={formData.agent_id?.toString() || ''} 
                                                    onValueChange={(value) => setFormData(prev => ({ ...prev, agent_id: value ? parseInt(value) : null }))}
                                                >
                                                    <SelectTrigger className="h-9">
                                                        <SelectValue placeholder="Select WhatsApp agent" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {whatsAppAgents.map((agent) => (
                                                            <SelectItem key={agent.id} value={agent.id.toString()}>
                                                                {agent.name} ({agent.display_id})
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <p className="text-xs text-muted-foreground">Choose agent for this template</p>
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <Label className="text-sm font-medium">Template Components *</Label>
                                            <div className="border rounded-lg p-3 bg-muted/30">
                                                <WhatsAppTemplateBuilder
                                                    components={formData.components}
                                                    onChange={(components) => setFormData(prev => ({ ...prev, components }))}
                                                />
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                Build your template with different components. Body component is required.{' '}
                                                <a href="https://docs.360dialog.com/partner/messaging/template-messages" target="_blank" rel="noopener noreferrer" 
                                                   className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline">
                                                    View examples
                                                </a>
                                            </p>
                                        </div>

                                        </form>
                                    </div>

                                    {/* Footer */}
                                    <div className="border-t bg-muted/20 px-6 py-4">
                                        <div className="flex justify-end gap-3">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setIsCreateDialogOpen(false)}
                                                disabled={isCreating}
                                            >
                                                Cancel
                                            </Button>
                                            <Button 
                                                size="sm"
                                                onClick={handleSubmit}
                                                disabled={isCreating}
                                                className="bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700 text-white gap-2"
                                            >
                                                {isCreating ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                        Submitting...
                                                    </>
                                                ) : (
                                                    'Submit for Approval'
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-6">
                    {isRefreshing ? (
                        <div className="rounded-lg border bg-card overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-b bg-muted/30 hover:bg-muted/30">
                                        <TableHead className="font-semibold text-foreground h-12">Name</TableHead>
                                        <TableHead className="font-semibold text-foreground">Category</TableHead>
                                        <TableHead className="font-semibold text-foreground">Language</TableHead>
                                        <TableHead className="font-semibold text-foreground">Status</TableHead>
                                        <TableHead className="font-semibold text-foreground">Available to</TableHead>
                                        <TableHead className="font-semibold text-foreground w-[100px]">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                    <TableBody>
                                        {[...Array(3)].map((_, i) => (
                                            <TableRow key={i}>
                                                <TableCell><div className="h-4 bg-muted rounded animate-pulse w-32"></div></TableCell>
                                                <TableCell><div className="h-4 bg-muted rounded animate-pulse w-20"></div></TableCell>
                                                <TableCell><div className="h-4 bg-muted rounded animate-pulse w-12"></div></TableCell>
                                                <TableCell><div className="h-6 bg-muted rounded animate-pulse w-20"></div></TableCell>
                                                <TableCell><div className="h-4 bg-muted rounded animate-pulse w-24"></div></TableCell>
                                                <TableCell><div className="h-8 w-8 bg-muted rounded animate-pulse"></div></TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                        </div>
                    ) : templates.length === 0 ? (
                        <div className="text-center py-8">
                            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                            <h3 className="font-medium mb-2">No templates yet</h3>
                            <p className="text-sm text-muted-foreground mb-4">
                                Create your first WhatsApp template to get started
                            </p>
                        </div>
                    ) : (
                        <div className="rounded-lg border bg-card overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-b bg-muted/30 hover:bg-muted/30">
                                        <TableHead className="font-semibold text-foreground h-12">Name</TableHead>
                                        <TableHead className="font-semibold text-foreground">Category</TableHead>
                                        <TableHead className="font-semibold text-foreground">Language</TableHead>
                                        <TableHead className="font-semibold text-foreground">Status</TableHead>
                                        <TableHead className="font-semibold text-foreground">Available to</TableHead>
                                        <TableHead className="font-semibold text-foreground w-[100px]">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {templates.map((template) => (
                                        <TableRow key={template.id} className="hover:bg-muted/50 transition-colors">
                                            <TableCell className="font-medium py-4">
                                                <button
                                                    onClick={() => onViewTemplate(template.id)}
                                                    className="text-left hover:text-primary transition-colors cursor-pointer flex items-center gap-2"
                                                >
                                                    {template.friendly_name || template.name}
                                                    <ExternalLink className="w-3 h-3 opacity-50" />
                                                </button>
                                            </TableCell>
                                            <TableCell className="py-4">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                                                    {template.category}
                                                </span>
                                            </TableCell>
                                            <TableCell className="py-4">
                                                <span className="text-sm font-mono font-medium">
                                                    {template.language ? template.language.toUpperCase() : '-'}
                                                </span>
                                            </TableCell>
                                            <TableCell className="py-4">{getStatusBadge(template.status)}</TableCell>
                                            <TableCell className="text-sm text-muted-foreground py-4">
                                                {getAgentNames(template.agent_ids)}
                                            </TableCell>
                                            <TableCell className="py-4">
                                                <div className="flex items-center gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => onViewTemplate(template.id)}
                                                        className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-950/20 dark:hover:text-blue-400"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                    <AlertDialog>
                                                        <AlertDialogTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                disabled={isDeleting === template.id}
                                                                className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/20 dark:hover:text-red-400"
                                                            >
                                                                {isDeleting === template.id ? (
                                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                                ) : (
                                                                    <Trash2 className="w-4 h-4" />
                                                                )}
                                                            </Button>
                                                        </AlertDialogTrigger>
                                                    <AlertDialogContent>
                                                        <AlertDialogHeader>
                                                            <AlertDialogTitle>Delete Template</AlertDialogTitle>
                                                            <AlertDialogDescription>
                                                                Are you sure you want to delete the template "{template.name}"? This action cannot be undone.
                                                            </AlertDialogDescription>
                                                        </AlertDialogHeader>
                                                        <AlertDialogFooter>
                                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                            <AlertDialogAction
                                                                onClick={() => handleDelete(template.id)}
                                                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                                            >
                                                                Delete
                                                            </AlertDialogAction>
                                                        </AlertDialogFooter>
                                                    </AlertDialogContent>
                                                </AlertDialog>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </motion.div>
    );
};
